import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import { useNavigate, useParams } from 'react-router';
import { getUserById } from '../../redux/actions/user/getUserById';
import { useDispatch, useSelector } from 'react-redux';
import Input from '../../components/Input/Input';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { resetPasswordUser } from '../../redux/actions/user/resetPasswordUser';
import { resetGetUserById, resetResetPasswordUser, resetUpdateEmailUser, resetUpdateUser, resetGetUserPermissions, resetUpdateUserPermissions } from '../../redux/slices/user.slice';
import { updateEmailUser } from '../../redux/actions/user/updateEmailUser';
import { updateUser } from '../../redux/actions/user/updateUser';
import Loading from '../../components/Loading/Loading';
import PermissionsCheckboxList from '../../components/Permissions/PermissionsCheckboxList';
import { getUserPermissions } from '../../redux/actions/user/getUserPermissions';
import { updateUserPermissions } from '../../redux/actions/user/updateUserPermissions';

function EditUser() {
  const [fileList, setFileList] = useState([]);
  const [totalEditState, setTotalEditState] = useState(false);
  const [permissionsEditState, setPermissionsEditState] = useState(false);

  const form = useForm({
    defaultValues: {
      email: '',
      fio: '',
      permissions: {},
    },
  });

  const navigate = useNavigate();
  const { userId } = useParams();
  const dispatch = useDispatch();

  const onChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const onPreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const {
    getUserById: { data: getUserByIdData, error: getUserByIdError, loading: getUserByIdLoading },
    resetPasswordUser: { data: resetPasswordUserData, error: resetPasswordUserError, loading: resetPasswordUserLoading },
    updateUser: { data: updateUserData, error: updateUserError, loading: updateUserLoading },
    updateEmailUser: { data: updateEmailUserData, error: updateEmailUserError, loading: updateEmailUserLoading },
    getUserPermissions: { data: userPermissionsData, loading: userPermLoading },
    updateUserPermissions: { data: updatePermData, loading: updatePermLoading },
  } = useSelector((state) => state.user);

  // Загружаем данные пользователя и его полномочия
  useEffect(() => {
    dispatch(getUserById(userId));
    dispatch(getUserPermissions(userId));

    return () => {
      form.reset();
      dispatch(resetGetUserById());
      dispatch(resetGetUserPermissions());
    };
  }, [userId]);

  // Заполняем форму данными пользователя
  useEffect(() => {
    if (getUserByIdData) {
      form.setValue('email', getUserByIdData?.email);
      form.setValue('fio', getUserByIdData?.full_name);
    }
  }, [getUserByIdData]);

  // Обработка успешного обновления данных
  useEffect(() => {
    if (updateUserData && updateEmailUserData) {
      Swal.fire({
        title: 'Данные пользователя успешно изменены',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetUpdateEmailUser());
      dispatch(resetUpdateUser());
      dispatch(getUserById(userId));
      setTotalEditState(false);
    }
  }, [updateUserData, updateEmailUserData]);

  // Обработка успешного обновления полномочий
  useEffect(() => {
    if (updatePermData) {
      Swal.fire({
        title: 'Полномочия пользователя успешно обновлены',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetUpdateUserPermissions());
      dispatch(getUserPermissions(userId));
      setPermissionsEditState(false);
    }
  }, [updatePermData]);

  // Сброс пароля
  const handleResetPassword = () => {
    Swal.fire({
      title: 'Вы действительно хотите сбросить пароль пользователя?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(resetPasswordUser(userId));
      }
    });
  };

  useEffect(() => {
    if (resetPasswordUserData) {
      Swal.fire({
        title: 'Новый пароль отправлен на почту',
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
      });
      dispatch(resetResetPasswordUser());
    }
  }, [resetPasswordUserData]);

  // Сохранение основных данных
  const onSubmit = (data) => {
    Swal.fire({
      title: 'Вы действительно хотите изменить данные пользователя?',
      text: '',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(updateEmailUser({ id: userId, email: data.email }));
        dispatch(updateUser({ id: userId, fullName: data.fio, phone: getUserByIdData?.phone }));
      }
    });
  };

  // Сохранение полномочий
  const onSavePermissions = () => {
    const permissionsData = form.getValues('permissions');
    let permissionsArray = Object.entries(permissionsData)
      .filter(([_, checked]) => checked)
      .map(([name]) => ({ name }));

    Swal.fire({
      title: 'Вы действительно хотите изменить полномочия пользователя?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Да',
      cancelButtonText: 'Нет',
    }).then((result) => {
      if (result.isConfirmed) {
        console.log(permissionsArray);

        permissionsArray.push({ name: 'view_user' });
        if (permissionsArray.find(({ name }) => name == 'add_manager')) {
          permissionsArray.push({ name: 'add_user' });
          permissionsArray.push({ name: 'change_user_permissions' });
          permissionsArray.push({ name: 'view_permissions' });
        }
        dispatch(updateUserPermissions({ userId, permissions: permissionsArray }));
      }
    });
  };

  const isLoading = updateUserLoading || updateEmailUserLoading || resetPasswordUserLoading || updatePermLoading;
  const isStaffUser = getUserByIdData?.type === 'staff';

  return (
    <div className="w-full flex flex-1 mt-10 p-1">
      <div className="container relative flex flex-1 flex-col mx-auto max-w-[1163px]">
        <div className="flex flex-col sm:flex-row w-full gap-3">
          <div className="flex w-full sm:w-[20%] mb-2 justify-center">
            {totalEditState ? (
              <div className="flex justify-center p-5">
                <Upload action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188" listType="picture-card" fileList={fileList} onChange={onChange} onPreview={onPreview}>
                  {fileList.length < 1 && 'Выгрузить'}
                </Upload>
              </div>
            ) : (
              <div className="bg-[#E5E5E5] w-[160px] h-[160px] m-5 flex items-center justify-center rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 24 24">
                  <path fill="rgba(0,0,0,0.6)" d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4" />
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-col w-full sm:w-[50%] mb-2 p-5 gap-3 relative">
            {isLoading && <Loading />}

            <div className="">{totalEditState ? <Input form={form} name="fio" placeholder="Имя" disabled={isLoading} /> : <label className="text-lg">{getUserByIdData?.full_name}</label>}</div>

            <div className="">
              <Input
                form={form}
                name="email"
                placeholder="Email"
                rules={{
                  required: { value: true, message: '*Обязательное поле для заполнения' },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Неверный формат почты',
                  },
                }}
                disabled={!totalEditState || isLoading}
              />
            </div>

            {totalEditState && (
              <button onClick={handleResetPassword} type="button" className="text-center text-[14px] border rounded py-2 px-4 text-white hover:bg-red-600 bg-red-500 w-[130px]" disabled={isLoading}>
                Сброс пароля
              </button>
            )}

            {!totalEditState ? (
              <>
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={() => setTotalEditState(true)}>
                  Редактировать
                </button>
                <button onClick={() => navigate(-1)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300">
                  Назад
                </button>
              </>
            ) : (
              <>
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={form.handleSubmit(onSubmit)} disabled={isLoading}>
                  Сохранить
                </button>
                <button onClick={() => setTotalEditState(false)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300" disabled={isLoading}>
                  Отмена
                </button>
              </>
            )}

            {/* Секция полномочий для staff пользователей */}
            {isStaffUser && (
              <>
                <hr className="my-4" />
                <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov w-full md:w-auto transition ease-in-out duration-300" onClick={() => setPermissionsEditState(!permissionsEditState)}>
                  {permissionsEditState ? 'Скрыть полномочия' : 'Полномочия'}
                </button>

                {permissionsEditState && (
                  <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                    <PermissionsCheckboxList form={form} name="permissions" userPermissions={userPermissionsData || []} disabled={isLoading} />
                    <div className="flex gap-3 mt-4">
                      <button type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov transition ease-in-out duration-300" onClick={onSavePermissions} disabled={isLoading}>
                        Сохранить полномочия
                      </button>
                      <button type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 hover:text-white bg-white hover:bg-button transition ease-in-out duration-300" onClick={() => setPermissionsEditState(false)} disabled={isLoading}>
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditUser;
